import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/client';
import MessageSequenceDisplay from '../../components/campaigns/MessageSequenceDisplay';
import PresetFilterModal from '../../components/modals/PresetFilterModal';
import DripifyMappingsPanel from '../../components/campaigns/DripifyMappingsPanel';
import { useAuthSession } from '../../contexts/AuthSessionContext';

interface CampaignDetails {
  id: string;
  name: string;
  status: string;
  service_tier: string;
  sequence_id?: string;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  lead_source_type?: string;
  lead_source_id?: string;
  target_audience_type?: string;
  user_id: string;
  assigned_csr: string;
  users?: {
    full_name: string;
    email: string;
  };
  companies?: {
    name: string;
  };
  campaign_sequences?: {
    sequence_name: string;
    messages: Array<{
      message_number: number;
      content: string;
      character_count: number;
      delay_days?: number;
    }>;
  };
  preset_filter_requests?: Array<{
    id: string;
    request_name: string;
    filters: any;
    status: string;
    target_lead_count?: number;
  }>;
}

const CSRCampaignDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuthSession();
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    loadCampaignDetails();
  }, [id]);

  const loadCampaignDetails = async () => {
    try {
      const { data: campaignData, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          users:user_id (
            full_name,
            email
          ),
          companies:company_id (
            name
          ),
          campaign_sequences!sequence_id (
            sequence_name,
            messages
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading campaign:', error);
        return;
      }

      // Load preset filter data if applicable
      let filterData = null;
      if (campaignData.lead_source_type === 'preset_filter' || campaignData.lead_source_type === 'preset_filters') {
        // For Target Leads, fetch by campaign_id
        const { data: filters } = await supabase
          .from('preset_filter_requests')
          .select('*')
          .eq('campaign_id', id);
        filterData = filters;
      } else if (campaignData.lead_source_type === 'past_list' && campaignData.lead_source_id) {
        // For Previous Target Lists, fetch by id
        const { data: filter } = await supabase
          .from('preset_filter_requests')
          .select('*')
          .eq('id', campaignData.lead_source_id)
          .single();
        filterData = filter ? [filter] : null;
      }

      setCampaign({
        ...campaignData,
        preset_filter_requests: filterData
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      running: 'bg-green-100 text-green-800',
      completed: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  // EPOCH 63: Renamed "JUNIOR SDR" to "PLATFORM"
  const getServiceTierBadge = (tier: string) => {
    return tier === 'top_qualifier' ? (
      <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
        TOP QUALIFIER
      </span>
    ) : (
      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
        PLATFORM
      </span>
    );
  };

  const getLeadSourceDisplay = () => {
    if (!campaign) return 'Not specified';
    
    const leadMethod = campaign.lead_source_type;
    const targetType = campaign.target_audience_type;
    
    if (leadMethod === 'preset_filter' || leadMethod === 'preset_filters') {
      return '🎯 Target Lead List';
    } else if (leadMethod === 'past_list') {
      return '📋 Previous Target List';
    } else if (leadMethod === 'csv_upload') {
      return '📁 CSV Upload';
    } else if (leadMethod === 'lead_generator') {
      return '🤖 Lead Generator';
    } else if (targetType === 'preset_filter' || targetType === 'preset_filters') {
      return '🎯 Target Lead List';
    } else if (targetType === 'past_list') {
      return '📋 Previous Target List';
    } else if (targetType === 'csv_upload') {
      return '📁 CSV Upload';
    }
    
    return 'Not specified';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const approveCampaign = async () => {
    if (!campaign) return;
    setProcessingAction(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'running',
          approved_at: new Date().toISOString(),
          approved_by: session.user.id,
          locked_at: new Date().toISOString()
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error approving campaign:', error);
        alert('Failed to approve campaign. Please try again.');
        return;
      }

      // Create notification for user
      if (campaign.users?.id) {
        await supabase
          .from('notifications')
          .insert({
            user_id: campaign.user_id,
            type: 'campaign_approved',
            title: 'Campaign Approved!',
            message: `Your campaign "${campaign.name}" has been approved and is now running.`,
            data: { 
              campaign_id: campaign.id,
              campaign_name: campaign.name,
              action_url: '/campaigns'
            }
          });
      }

      alert('Campaign approved successfully!');
      navigate('/csr-campaigns');
    } catch (error) {
      console.error('Error in approveCampaign:', error);
      alert('Failed to approve campaign. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const requestEdits = async () => {
    if (!campaign) return;
    setProcessingAction(true);
    
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'draft',
          submitted_at: null
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error requesting edits:', error);
        alert('Failed to request edits. Please try again.');
        return;
      }

      // Create notification for user
      if (campaign.users?.id) {
        await supabase
          .from('notifications')
          .insert({
            user_id: campaign.user_id,
            type: 'campaign_needs_editing',
            title: 'Campaign Needs Editing',
            message: `Your campaign "${campaign.name}" requires changes before approval. Please review and resubmit.`,
            data: { 
              campaign_id: campaign.id,
              campaign_name: campaign.name,
              action_url: `/campaigns/edit/${campaign.id}`
            }
          });
      }

      alert('Campaign sent back for editing.');
      navigate('/csr-campaigns');
    } catch (error) {
      console.error('Error in requestEdits:', error);
      alert('Failed to request edits. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const hasFilterData = () => {
    return campaign?.preset_filter_requests && campaign.preset_filter_requests.length > 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Campaign not found or you don't have permission to view it.
        </div>
        <button
          onClick={() => navigate('/csr-campaigns')}
          className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/csr-campaigns')}
          className="mb-4 text-purple-600 hover:text-purple-800 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Campaigns
        </button>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{campaign.name}</h1>
              <div className="flex items-center space-x-3">
                {getStatusBadge(campaign.status)}
                {getServiceTierBadge(campaign.service_tier)}
                <span className="text-sm text-gray-500">
                  Lead Source: {getLeadSourceDisplay()}
                </span>
              </div>
            </div>
          </div>

          {/* Campaign Info */}
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <span className="font-medium text-gray-600">Client:</span>
              <p className="text-gray-900">
                {campaign.users?.full_name} ({campaign.users?.email})
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Company:</span>
              <p className="text-gray-900">{campaign.companies?.name || 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Created:</span>
              <p className="text-gray-900">{formatDate(campaign.created_at)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Submitted:</span>
              <p className="text-gray-900">{formatDate(campaign.submitted_at)}</p>
            </div>
            {campaign.approved_at && (
              <div>
                <span className="font-medium text-gray-600">Approved:</span>
                <p className="text-gray-900">{formatDate(campaign.approved_at)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lead Source Details */}
      {(getLeadSourceDisplay().includes('Target') || getLeadSourceDisplay().includes('Previous')) && hasFilterData() && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Lead Source Details</h2>
            <button
              onClick={() => setShowFilterModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              View Filters
            </button>
          </div>
          {campaign.preset_filter_requests && campaign.preset_filter_requests[0] && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Request Name:</span>
                <p className="text-gray-900">{campaign.preset_filter_requests[0].request_name}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <p className="text-gray-900">{campaign.preset_filter_requests[0].status || 'Pending'}</p>
              </div>
              {campaign.preset_filter_requests[0].target_lead_count && (
                <div>
                  <span className="font-medium text-gray-600">Target Lead Count:</span>
                  <p className="text-gray-900">{campaign.preset_filter_requests[0].target_lead_count.toLocaleString()}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Message Sequence */}
      {campaign.campaign_sequences?.messages && campaign.campaign_sequences.messages.length > 0 ? (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <MessageSequenceDisplay 
            messages={campaign.campaign_sequences.messages}
            title="LinkedIn Message Sequence"
            showBackground={false}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">LinkedIn Message Sequence</h2>
          <p className="text-gray-500">No message sequence found for this campaign.</p>
          {campaign.sequence_id && (
            <p className="text-xs text-gray-400 mt-2">Sequence ID: {campaign.sequence_id}</p>
          )}
        </div>
      )}

      {/* Dripify Response Automation -- CSR/admin only, never shown to the customer viewing their own campaign */}
      {(userRole === 'csr' || userRole === 'admin' || userRole === 'super_admin') && (
        <DripifyMappingsPanel campaignId={campaign.id} />
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end space-x-3">
        {campaign.status === 'submitted' && (
          <>
            <button
              onClick={requestEdits}
              disabled={processingAction}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingAction ? 'Processing...' : 'Request Edits'}
            </button>
            <button
              onClick={approveCampaign}
              disabled={processingAction}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingAction ? 'Processing...' : 'Approve & Launch'}
            </button>
          </>
        )}
      </div>

      {/* Preset Filter Modal */}
      {showFilterModal && hasFilterData() && (
        <PresetFilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          filterData={campaign.preset_filter_requests![0]}
          campaignName={campaign.name}
        />
      )}
    </div>
  );
};

export default CSRCampaignDetails;